import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import {
  difference as _difference,
  get as _get,
  includes as _includes,
  some as _some,
} from 'lodash';
import xssFilters from 'xss-filters';

import { dismissBannerMessage as trackDismissBannerMessage} from 'analytics/messaging';
import {
  fetchNotifications,
  hideNotification,
  showNotification,
  withinCourierCheck,
} from 'actions';

import { locale } from 'utils/locale';
import { currencyFormat } from 'utils/currencyFormat';
import { optionalAttribute } from 'utils/jsx';
import { parameterize } from 'utils/queryparams';

import CookieBanner from 'components/CookieBanner/CookieBanner';
import CreditCampaignBanner from 'components/CreditCampaignBanner/CreditCampaignBanner';
import localize from 'components/Localize/Localize';
import Modal from 'components/Modal/Modal';
import Notification from 'components/Notification/Notification';

import styles from './NotificationContainer.css';
import translations from './translations';

class NotificationContainer extends React.Component {
  componentWillMount() {
    this.props.withinCourierCheck();
    this.props.fetchNotifications(this.props.locale);
  }

  componentDidMount() {
    this.referralRedirectNotification();
    this.referralOTGRedirectNotification();

    const {history} = this.context.router;

    history.listen(this.handleBannerChange.bind(this));
  }

  handleBannerChange() {
    this.props.siteBanners.forEach((banner) => {
      if (this.isNotificationEligible(banner)) {
        this.props.showNotification(banner);
      } else {
        this.props.hideNotification(banner);
      }
    });
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.siteBanners !== this.props.siteBanners) {
      nextProps.siteBanners.forEach((banner) => {
        if (this.isNotificationEligible(banner)) {
          this.props.showNotification(banner);
        }
      });
    }

    const userNotificationsAdded = _difference(nextProps.userNotifications, this.props.userNotifications);
    const userNotificationsRemoved = _difference(this.props.userNotifications, nextProps.userNotifications);

    // If notification has been added to userNotifications, show it
    if (userNotificationsAdded.length) {
      userNotificationsAdded.forEach((banner) => {
        this.props.showNotification(banner);
      });
    }

    // If notification has been removed from userNotifications, hide it
    if (userNotificationsRemoved.length) {
      userNotificationsRemoved.forEach((banner) => {
        this.props.hideNotification(banner);
      });
    }
  }

  // Promo notifications
  getMessage(params) {
    const partner = xssFilters.inHTMLData(params.partner_hero_name.replace(/[+]/g, ` `));
    const partnerType = xssFilters.inHTMLData(params.partner_hero_type);
    const code = xssFilters.inHTMLData(params.coupon_code);
    const savings = xssFilters.inHTMLData(params.coupon_code_amount);
    const medium = xssFilters.inHTMLData(params.utm_medium.replace(/[+]/g, ` `));
    const promoType = xssFilters.inHTMLData(params.promo_type);

    const partnerTypeString = partnerType === `undefined` ? `` : ` ${partnerType.replace(/[+]/g, ` `)}`;

    const isBlackListed = _includes([`sponsored`], medium);

    let msg;

    if (isBlackListed) {
      return null;
    }

    switch (promoType) {
      case `wave`: {
        msg = this.props.t.waveTemplate;
        break;
      }
      case `basket`: {
        const basketThreshold = xssFilters.inHTMLData(params.basket_threshold);
        if (basketThreshold === `undefined`) {
          return null;
        } else {
          msg = this.props.t.basketTemplate.replace(`[basket_threshold]`, currencyFormat(basketThreshold, this.props.locale, false));
        }
        break;
      }
      case `product`: {
        const productName = xssFilters.inHTMLData(params.product_name);
        if (productName === `undefined`) {
          return null;
        } else {
          msg = this.props.t.productTemplate.replace(`[product_name]`, productName.replace(/[+]/g, ` `));
        }
        break;
      }
      default:
        msg = this.props.t.standardTemplate;
    }

    return msg.replace(`[partner]`, partner).replace(`[partner_type]`, partnerTypeString).replace(`[code]`, code).replace(`[savings]`, currencyFormat(savings, this.props.locale, false));
  }

  referralRedirectNotification() {
    const params = parameterize(window.location.search);

    if (!(params && params.partner_hero_name && params.coupon_code && params.coupon_code_amount && params.utm_medium)) {
      return null;
    }

    if (!this.getMessage(params)) {
      return null;
    }

    const bannerData = {
      attributes: {
        name: `redirectBanner`,
        'banner-type': `promo`,
        message: this.getMessage(params),
      },
    };

    this.props.showNotification(bannerData);
  }

  referralOTGRedirectNotification() {
    const currentLocale = locale(this.props.locale);

    if (!currentLocale.isEnglish()) {
      return null;
    }

    const params = parameterize(window.location.search);

    if (!(params && params.utm_source && params.utm_source === `OTG`) && !(params && params.utm_content && params.utm_content.includes(`Welcome_2_MeetTheTeam_OTG`))) {
      return null;
    }

    const msg = `${this.props.t.greetingOTG} <span class="referral-msg-landing-code"> FLY20 </span> ${this.props.t.towardsOTG}`;

    const bannerData = {
      attributes: {
        name: `redirectBanner`,
        'banner-type': `promo`,
        message: msg,
      },
    };

    this.props.showNotification(bannerData);
  }

  // regular notifications
  getBannerAcknowledged(bannerID) {
    if (!window.sessionStorage) {
      return;
    }

    return Boolean(JSON.parse(window.sessionStorage.getItem(bannerID)));
  }

  dismissBanner(bannerName, bannerID) {
    const path = window.location.pathname;

    this.setBannerAcknowledged(bannerID);
    trackDismissBannerMessage(path, bannerName);
  }

  setBannerAcknowledged(bannerID) {
    if (!window.sessionStorage) {
      return;
    }

    window.sessionStorage.setItem(bannerID, true);
  }

  getBannerIDString(banner) {
    return (banner.attributes[`start-time`] + banner.attributes[`end-time`] + banner.attributes[`name`]).split(` `).join(`-`);
  }

  matchesURL(urlPattern) {
    // @TODO: If regex is invalid, it breaks all notifications. This is currently a failsafe.
    // We should find a way to validate regex before being saved in the DB in Rails
    try {
      const patternRegex = new RegExp(urlPattern);

      return patternRegex.test(window.location);
    } catch (e) {
      return false;
    }
  }

  isNotificationEligible(banner) {
    const notForThisLocale = !_some(banner.attributes[`store-locales`], { locale: this.props.locale });
    const noURLPatternMatch = !this.matchesURL(banner.attributes[`url-pattern`]);
    const matchesExcludeURLPattern = banner.attributes[`exclude-url-pattern`] !== `` && this.matchesURL(banner.attributes[`exclude-url-pattern`]);
    const courierCitiesOnly = !this.props.withinCourier && banner.attributes[`courier-cities-only`];
    const bannerIDString = this.getBannerIDString(banner);
    const bannerAcknowledged = this.getBannerAcknowledged(bannerIDString);

    if (notForThisLocale || noURLPatternMatch || matchesExcludeURLPattern || courierCitiesOnly || bannerAcknowledged) {
      return false;
    }

    return true;
  }

  renderModal(banner) {
    if (!banner.attributes[`is-modal`]) {
      return null;
    }

    // TODO: Update siteBanners API to pass in alt text
    // TODO: Use <ResponsiveImage> instead
    return (
      <Modal
        keyName={banner.attributes.name}
        type="marketing"
      >
        <div>
          <a href={banner.attributes.link}>
            <picture>
              <source
                media="(min-width: 420px)"
                srcSet={banner.attributes[`desktop-image`]} />
              <img
                alt=""
                src={banner.attributes[`mobile-image`]}
              />
            </picture>
          </a>
          <p
            className="disclaimer"
            dangerouslySetInnerHTML={{__html: banner.attributes[`disclaimer`]}}
          />
        </div>
      </Modal>
    );
  }

  renderBanner(banner) {
    const today = new Date();
    const startTime = new Date(banner.attributes[`start-time`]);
    const endTime = new Date(banner.attributes[`end-time`]);

    if (today <= startTime || today >= endTime) {
      return;
    }

    const bannerIDString = this.getBannerIDString(banner);

    return (
      <div key={`notification-${banner.attributes.name}`}>
        <Notification
          keyName={banner.attributes.name}
          onClose={this.dismissBanner.bind(this, banner.attributes.message, bannerIDString)}
        />
        {this.renderModal(banner)}
      </div>
    );
  }

  renderUserNotifications() {
    if (!this.props.userNotifications) {
      return null;
    }

    return (
      this.props.userNotifications.map((banner) => {
        const bannerIDString = banner.attributes.name.split(` `).join(`-`);

        return (
          <Notification
            keyName={banner.attributes.name}
            onClose={this.dismissBanner.bind(this, banner.attributes.message, bannerIDString)}
            type={banner.attributes.banner_type}
          />
        );
      })
    );
  }

  renderSiteBanners() {
    if (!this.props.siteBanners) {
      return null;
    }

    return (
      this.props.siteBanners.map((banner) => {
        return this.renderBanner(banner);
      })
    );
  }

  renderReferralInvalidCodeNotification() {
    if (!this.props.referralInvalidCodeMsg) {
      return null;
    }

    return (
      <Notification keyName="referralInvalidCodeBanner" />
    );
  }

  renderRegisterNotification() {
    if (!this.props.registerMessage) {
      return null;
    }

    return (
      <Notification keyName="registerBanner" />
    );
  }

  renderReferralRedirectNotification() {
    if (!this.props.redirectBannerMsg) {
      return null;
    }

    return (
      <Notification keyName="redirectBanner" />
    );
  }

  renderReviewComposerNotification() {
    const params = parameterize(window.location.search);

    if (!params.referral_review || this.getBannerAcknowledged(`referral_review`)) {
      return null;
    }

    const bannerData = {
      attributes: {
        name: `redirectBanner`,
        'banner-type': `promo`,
        message: this.props.t.reviewReferral,
      },
    };

    this.props.showNotification(bannerData);
    this.setBannerAcknowledged(`referral_review`);
  }

  renderNotifications() {
    if (this.props.redirectBannerMsg) {
      return (
        this.renderReferralRedirectNotification()
      );
    } else {
      return (
        [
          this.renderUserNotifications(),
          this.renderSiteBanners(),
          this.renderRegisterNotification(),
          this.renderReferralInvalidCodeNotification(),
          this.renderReviewComposerNotification(),
        ]
      );
    }
  }

  render() {
    const className = optionalAttribute(`className`, styles[`container--fixed`], this.props.isFixed);

    return (
      <div {...className}>
        <CookieBanner />
        <CreditCampaignBanner />
        {this.renderNotifications()}
      </div>
    );
  }
}

NotificationContainer.defaultProps = {
  isFixed: false,
};

NotificationContainer.contextTypes = {
  router: PropTypes.object,
};

NotificationContainer.propTypes = {
  isFixed: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  redirectBannerMsg: PropTypes.string,
  referralInvalidCodeMsg: PropTypes.string,
  registerMessage: PropTypes.string,
  siteBanners: PropTypes.array,
  t: PropTypes.shape({
    basketTemplate: PropTypes.string,
    greetingOTG: PropTypes.string,
    productTemplate: PropTypes.string,
    standardTemplate: PropTypes.string,
    towardsOTG: PropTypes.string,
    waveTemplate: PropTypes.string,
    reviewReferral: PropTypes.string,
  }),
  userNotifications: PropTypes.array,
  withinCourier: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  locale: _get(state, `i18n.locale`),
  redirectBannerMsg: _get(state, `notifications.redirectBanner.message`),
  referralInvalidCodeMsg: _get(state, `notifications.referralInvalidCodeBanner.message`),
  registerMessage: _get(state, `notifications.registerBanner.message`),
  siteBanners: _get(state, `notifications.siteBanners`, []),
  userNotifications: _get(state, `notifications.userNotifications`, []),
  withinCourier: _get(state, `courier.withinCourier`),
});

const mapDispatchToProps = (dispatch) => ({
  fetchNotifications: (locale) => {
    dispatch(fetchNotifications(locale));
  },
  hideNotification: (data) => {
    dispatch(hideNotification(data.attributes.name));
  },
  showNotification: (data) => {
    dispatch(showNotification(data.attributes.name, { type: data.attributes[`banner-type`], message: data.attributes.message }));
  },
  withinCourierCheck: () => {
    dispatch(withinCourierCheck());
  },
});

export default localize(connect(mapStateToProps, mapDispatchToProps)(NotificationContainer), translations);
